<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import { equipCollection } from '@/core/equip'
import type { EquipItemType } from '@/types/equip'
import type { ItemType } from '@/types/item'
import { computed } from 'vue'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import type { ConsumablesModeType } from '@/types/consumables'
import type { PetType } from '@/types/pet'
import { getPetName } from '@/views/pet-ranch/data'
import PetInfo from '@/views/pet-ranch/components/PetInfo.vue'
import type { RuneItemType } from '@/types/rune'
import { getRuneName } from '@/core/rune'
import RuneInfo from '@/views/item/components/RuneInfo.vue'
import { getEquipName } from '@/utils/equip'
import { treasuryInfoList } from '@/views/treasury/data'
import { formatNumberWithCommas } from '@/utils'
import type { RelicItemType } from '@/types/relic'
import { getRelicName } from '@/core/relic'
import RelicInfo from '@/views/item/components/RelicInfo.vue'
import SoulCurrency from '@/views/pvp-card/components/SoulCurrency.vue'
import type { SkillItemType } from '@/types/skill'
import { getSkillName } from '@/core/skill'
import SkillInfo from '@/views/item/components/SkillInfo.vue'

const props = defineProps<{
  receiveTitle: string
  receiveItem: ItemType[] | null
  receiveGold: number
  receiveDiamond: number
  receiveCrystal: number
  receiveDuelCrystal: number
  receiveConsumables?: Partial<Record<ConsumablesModeType, number>>
  receivePetList: PetType[] | null
  receiveRuneList: RuneItemType[] | null
  receiveRelicList: RelicItemType[] | null
  receiveSkillList: SkillItemType[] | null
}>()

const itemList = computed<EquipItemType[]>(() => {
  const items = props.receiveItem
  if (!items) return []
  return items.map((item) => {
    const { equipId } = item
    const { name, type, attrs } = equipCollection[equipId]
    return { ...item, name, type, attrs }
  })
})
</script>

<template>
  <div class="receive-item">
    <h2>恭喜获得</h2>
    <div class="receive-detail">
      <p v-show="receiveGold">💰 金币：{{ formatNumberWithCommas(receiveGold) }}</p>
      <p v-show="receiveDiamond">💎 钻石：{{ formatNumberWithCommas(receiveDiamond) }}</p>
      <p v-show="receiveCrystal">🔮 水晶：{{ formatNumberWithCommas(receiveCrystal) }}</p>
      <p v-show="receiveDuelCrystal"><SoulCurrency type="jade" size="16px" /> 魂玉：{{ formatNumberWithCommas(receiveDuelCrystal) }}</p>
      <template v-if="receiveConsumables">
        <p v-for="item of treasuryInfoList" v-show="(receiveConsumables[item.key] || 0) !== 0">{{ item.name }}：{{ receiveConsumables[item.key] }}</p>
      </template>
      <p v-if="receiveTitle === '秘藏洞窟奖励'">💲 活动积分：80</p>
    </div>
    <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
      <el-row :gutter="10">
        <el-col :span="12" v-for="item of itemList" :key="item.id">
          <el-popover placement="bottom" width="300px" trigger="click">
            <template #reference>
              <CommonBtn size="small">
                <span v-html="getEquipName(item, item.type)"></span>
              </CommonBtn>
            </template>
            <EquipInfo :equip-info="item" :show-action="false" style="max-height: 55vh" />
          </el-popover>
        </el-col>
        <el-col :span="12" v-for="item of receivePetList" :key="item.id">
          <el-popover placement="bottom" width="300px" trigger="click">
            <template #reference>
              <CommonBtn size="small">
                <span :class="item.quality">
                  {{ getPetName(item.monsterId) }}
                </span>
              </CommonBtn>
            </template>
            <PetInfo :pet-info="item" :show-action="false" />
          </el-popover>
        </el-col>
        <el-col :span="12" v-for="item of receiveRuneList" :key="item.id">
          <el-popover placement="bottom" width="300px" trigger="click">
            <template #reference>
              <CommonBtn size="small">
                <span :class="item.quality">
                  {{ getRuneName(item.runeId) }}
                </span>
              </CommonBtn>
            </template>
            <RuneInfo :rune-info="item" :show-action="false" />
          </el-popover>
        </el-col>
        <el-col :span="12" v-for="item of receiveRelicList" :key="item.id">
          <el-popover placement="bottom" width="300px" trigger="click">
            <template #reference>
              <CommonBtn size="small">
                <span :class="item.quality">
                  {{ getRelicName(item.relicId, 0) }}
                </span>
              </CommonBtn>
            </template>
            <RelicInfo :relic-info="item" :show-action="false" />
          </el-popover>
        </el-col>
        <el-col :span="12" v-for="item of receiveSkillList" :key="item.id">
          <el-popover placement="bottom" width="300px" trigger="click">
            <template #reference>
              <CommonBtn size="small">
                <span :class="item.quality" v-html="getSkillName(item)"></span>
              </CommonBtn>
            </template>
            <SkillInfo :skill-info="item" :show-action="false" />
          </el-popover>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.receive-item {
  .receive-detail {
    p {
      margin: 5px 0;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
